import { useMemo } from "react";
import { Tag } from "../models/tag";
import { tagsApi } from "../services/tags";
import { useWorkspaceCategories } from "./useWorkspaceCategories";

export type FilterOption = {
  include?: string[];
  exclude?: string[];
  includeAll?: boolean;
  workspaceId?: string;
};

export const useTagData = (tagIds?: string[], filterOption?: FilterOption) => {
  const { data: serverTags = [], isLoading: isLoadingTags } = tagsApi.useGetTagsQuery();
  const { workspaceCategories, isLoadingCategories } = useWorkspaceCategories(filterOption);
  const tags = useMemo(() => {
    if (tagIds) {
      return serverTags.filter((tag) => tagIds.includes(tag.tagId));
    }
    return serverTags;
  }, [serverTags, tagIds]);

  const { workspaceVisibleTags, workspaceHiddenTags } = useMemo(() => {
    const workspaceVisibleTags: Tag[] = [];
    const workspaceHiddenTags: Tag[] = [];

    if (filterOption?.include || filterOption?.exclude) {
      const { include = [], exclude = [] } = filterOption;

      tags.forEach((tag) => {
        const category = workspaceCategories.find((category) => category.tagCategoryId === tag.tagCategoryId);
        const categoryName = category?.name ?? "";

        const isIncluded = include.length === 0 || include.includes(categoryName);
        const isExcluded = exclude.includes(categoryName);

        if (category && isIncluded && !isExcluded) {
          workspaceVisibleTags.push(tag);
        } else {
          workspaceHiddenTags.push(tag);
        }
      });
    } else {
      tags.forEach((tag) => {
        const category = workspaceCategories.find((category) => category.tagCategoryId === tag.tagCategoryId);
        if (category) workspaceVisibleTags.push(tag);
        else workspaceHiddenTags.push(tag);
      });
    }

    return { workspaceVisibleTags, workspaceHiddenTags };
  }, [workspaceCategories, tags]);

  const workspaceVisibleTagCategories = useMemo(() => {
    const tagCategories: Record<string, Tag[]> = {};
    workspaceCategories.forEach((category) => {
      tagCategories[category.name] = workspaceVisibleTags.filter((tag) => tag.tagCategoryId === category.tagCategoryId);
    });
    return tagCategories;
  }, [workspaceCategories, workspaceVisibleTags]);

  return {
    tags,
    workspaceVisibleTags,
    workspaceHiddenTags,
    isLoading: isLoadingCategories || isLoadingTags,
    workspaceVisibleTagCategories,
    categories: workspaceCategories,
  };
};
